import { awaitCondition } from "../../../../../stream";
import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import openViewOnNode from "../front/openViewOnNode";
import { translate } from "../front/translate";
import { orderedAttributes } from "../graphIterators";
import { getGuiAction } from "../guiModel";
import { DatastoreProxy } from "../interface/DatastoreProxy";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { IdPathBuilder } from "../navigation/IdPathBuilder";
import SubscriptionBuilder from "../state 2 interface/SubscriptionBuilder";
import * as SubscriptionManager from "../state 2 interface/SubscriptionManager";
import * as Collection from "../state/Collection";
import * as expressions from "../state/expressions";
import * as Number from "../state/Number";
import * as Stategroup from "../state/Stategroup";
import * as Text from "../state/Text";
import { optional_variable_assignment, Variable } from "../state/Variable";
import { Pending, Unknown, Failed, isResolved, valueOrFailed } from "../Status";
import { promiseSeries } from "../utils";
import { getWindowInstance } from "../widgets/dom/Window";
import * as add from "../widgets/instructions/add";
import { deleteCollectionEntry } from "../widgets/instructions/delete";
import * as execute from "../widgets/instructions/execute";
import { saveAll } from "../widgets/instructions/save";
import * as update from "../widgets/instructions/update";
import * as addClientState from "./addClientState";
import { iCollection } from "../state/Collection";
import { iStategroup, iState } from "../state/Stategroup";
import { iText } from "../state/Text";
import { iNode } from "../state/Node";
import * as edit from "./editMode";
import { startOperation } from "./operations";
import {
	registerAction,
	registerCollection,
	registerCommand,
	registerGroup,
	registerIdPath,
	registerNewNode,
	registerNodeContext,
	registerNumber,
	registerStategroup,
	registerText,
	registerEntry,
	registrationInitialized,
} from "./registerClientState";
import clientWorkIsDone from "./tasks";
import api_cs = api_configuration.types.client_state.alan_api;
import api_gui = api_configuration.libraries.gui_definition_lib.types.gui_model.alan_api;
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { addMessage } from "../front/messages";
import { sendFeedback } from "../error-logging";

export async function executeAction(
	action: api_cs.Cyes__started,
	view_bindings: Record<string, api_views.Cview_bindings>,
	can_use_containing_view: boolean,
	view_node_type: api_app.Cnode__application
): Promise<void> {
	switch (action.properties.executing.state.name) {
		case "no": {
			action.properties.executing.set("yes", {
				"can continue": ["no", {}],
			});
			const node_context = action.parent.parent.parent;
			const node_context_modified = nodeModified(node_context);
			const history: History = { events: [] };
			const execution_destructor = new DestructorTree();
			startOperation(action.parent, execution_destructor);
			try {
				await executeActionStep({
					action,
					action_step: getGuiAction(action.parent.properties.type.cast("action").inferences.action_type())!.properties
						.action,
					view_bindings,
					node_context_modified,
					can_use_containing_view,
					view_node_type,
					history,
				});
				if (!action.destroyed) {
					action.properties.executing.set("no", {
						"last result": ["success", {}],
					});
				}
			} catch (reason) {
				if (reason === "Cancelled") {
					if (!action.destroyed) {
						action.properties.executing.set("no", {
							"last result": ["not invoked", {}],
						});
					}
				} else {
					if (!action.destroyed) {
						action.properties.executing.set("no", {
							"last result": ["failure", {}],
						});
					}
					throw reason;
				}
			} finally {
				execution_destructor.destroy();
				reinitializeOperation(action);
			}
			break;
		}
		case "yes": {
			break;
		}
		default:
			unexpectedState(action.properties.executing.state);
	}
}

function nodeModified(node_context: api_cs.Cnode) {
	switch (node_context.properties.location.state.name) {
		case "local": {
			return true;
		}
		case "remote": {
			switch (node_context.properties.modified.state.name) {
				case "no": {
					return false;
				}
				case "yes": {
					return true;
				}
				default:
					return unexpectedState(node_context.properties.modified.state);
			}
		}
		default:
			unexpectedState(node_context.properties.location.state);
	}
}

export function reinitializeOperation(action: api_cs.Cyes__started): void {
	if (action.properties.node.destroyed) return;
	iNode(action.properties.node).discard();
}

function operationVariableAssignment(operation: api_cs.Cyes__started): Variable | null {
	const node = operation.properties.node;
	const $type = operation.parent.properties.type.state;
	switch ($type.name) {
		case "command": {
			const $implementation = $type.node.inferences.command_type().properties.implementation.state;
			switch ($implementation.name) {
				case "external":
					return null;
				case "internal":
					return optional_variable_assignment($implementation.node.properties.variable_assignment, null, node);
				default:
					return unexpectedState($implementation);
			}
		}
		case "action":
			return optional_variable_assignment(
				$type.node.inferences.action_type().properties.variable_assignment,
				null,
				node
			);
		default:
			unexpectedState($type);
	}
}

async function executeActionStep({
	action,
	action_step,
	view_bindings,
	node_context_modified,
	can_use_containing_view,
	view_node_type,
	history,
}: {
	action: api_cs.Cyes__started;
	action_step: api_gui.Cgui_action;
	view_bindings: Record<string, api_views.Cview_bindings>;
	node_context_modified: boolean;
	can_use_containing_view: boolean;
	view_node_type: api_app.Cnode__application;
	history: History;
}): Promise<void> {
	const node_context = action.parent.parent.parent;
	const action_expression_tasks: ActionTask[] = [];
	const destructor = new DestructorTree();

	let last_step = false;
	switch (action_step.properties.has_next_action.state.name) {
		case "no": {
			last_step = true;
			break;
		}
		case "yes":
			break;
	}
	try {
		if (action.destroyed === true) {
			addMessage("warning", "Did not complete operation", { body: "The operation was lost before it was completed" });
			sendFeedback("Action context was destroyed before it was finished");
			return;
		}
		await executeActionExpression(
			action_step.properties.expression,
			interactionBuilders(
				action,
				view_bindings,
				view_node_type,
				{
					node_context_modified,
					can_use_containing_view,
					last_step,
				},
				(node_to_update) => {
					if (!can_use_containing_view) {
						return false;
					}
					return (
						last_step && actionBoundry(node_context, view_node_type) === actionBoundry(node_to_update, view_node_type)
					);
				},
				(action_view) => {
					action_expression_tasks.push(action_view);
				}
			),
			node_context,
			action,
			action.properties.node,
			operationVariableAssignment(action),
			destructor,
			view_bindings,
			history,
			false
		);
		for (const task of action_expression_tasks) {
			await task();
		}
	} finally {
		destructor.destroy();
	}
	switch (action_step.properties.has_next_action.state.name) {
		case "no":
			break;
		case "yes": {
			const $ = action_step.properties.has_next_action.state.node;
			await executeActionStep({
				action,
				action_step: $.properties.action,
				view_bindings,
				node_context_modified,
				can_use_containing_view,
				view_node_type,
				history,
			});
			break;
		}
		default:
			unexpectedState(action_step.properties.has_next_action.state);
	}
}

type ActionTask = () => Promise<void>;
type InteractionBuilder = {
	execute_action: (action_interaction: { action: api_cs.Cyes__started }) => void;
	execute_command: (command_interaction: api_cs.Cyes__started) => void;
	show_target: (
		show_target: api_app.Cui_action_visualization,
		action_view: api_gui.Cviews | undefined,
		client_context_node: api_cs.Cnode
	) => Promise<void>;
	update_properties: (update_properties_interaction: {
		interactive: api_app.Cui_action_interaction;
		action_view: api_gui.Cviews | undefined;
		target_node: api_cs.Cnode;
		destructor: DestructorTree;
		save: boolean;
		history: History;
		on_interactive: (view_context_node: api_cs.Cnode) => void;
	}) => Promise<any>;
};
function interactionBuilders(
	action: api_cs.Cyes__started,
	view_bindings: Record<string, api_views.Cview_bindings>,
	view_node_type: api_app.Cnode__application,
	flags: {
		node_context_modified: boolean;
		can_use_containing_view: boolean;
		last_step: boolean;
	},
	can_use_current_view: (context_node: api_cs.Cnode) => boolean,
	callback: (interaction: ActionTask, show_target?: boolean) => void
): InteractionBuilder {
	return {
		execute_action: (action_interaction) => {
			callback(() =>
				executeAction(action_interaction.action, view_bindings, flags.can_use_containing_view, view_node_type)
			);
		},
		execute_command: (command_interaction) => {
			callback(() =>
				command_interaction.parent.parent.parent.properties.location.switch({
					remote: () => execute.command(command_interaction),
					local: () =>
						//TODO make sure that the node is saved somehow
						Promise.resolve(),
				})
			);
		},
		show_target: async (show_target, action_view, client_context_node) => {
			const view_definition = action_view === undefined ? undefined : view_bindings[action_view.key];
			if (view_definition !== undefined) {
				switch (show_target.properties.show_target.state.name) {
					case "yes": {
						switch (view_definition.properties.can_open_entry.state.name) {
							case "yes": {
								const $ = view_definition.properties.can_open_entry.state.node;
								callback(async () => {
									const view_context_node = client_context_node.output
										.position()
										.output.root()
										.switch({
											root: () => client_context_node,
											operation: ($) => $.parent.parent.parent,
										});
									openViewOnNode(
										view_context_node.input.entity().output.node(),
										$.properties.view.ref,
										$.properties.window.ref
									);
								}, true);
								break;
							}
							case "no":
								break;
						}
						break;
					}
					case "no":
						break;
				}
			}
		},
		update_properties: async (update_properties_interaction) => {
			const view_context_node = update_properties_interaction.target_node.output
				.position()
				.output.root()
				.switch({
					root: () => update_properties_interaction.target_node,
					operation: ($) => $.parent.parent.parent,
				});
			const target_node = update_properties_interaction.target_node;
			let view_definition: api_views.Cview_bindings | undefined;
			if (update_properties_interaction.action_view !== undefined) {
				view_definition = view_bindings[update_properties_interaction.action_view.key];
			}
			await SubscriptionManager.finishedAndInitialized();
			const node_must_be_validated = target_node.properties.location.switch({
				local: ($) =>
					$.properties.parent_location.switch({
						remote: true,
						local: update_properties_interaction.target_node.output
							.position()
							.output.root()
							.switch({
								root: () => false,
								operation: () => true,
							}),
					}),
				remote: true,
			});

			callback(async () => {
				const onTargetDone = async () => {
					let should_save;
					if (!update_properties_interaction.save) {
						should_save = false;
					} else if (!node_must_be_validated) {
						should_save = false;
					} else {
						should_save = !flags.node_context_modified;
					}
					if (should_save) {
						switch (target_node.properties.is_valid.state.name) {
							case "no": {
								break;
							}
							case "yes": {
								await saveAll(target_node);
								if (!target_node.destroyed) {
									switch (target_node.properties.location.state.name) {
										case "local":
											break;
										case "remote": {
											edit.switchOff(target_node);
											break;
										}
										default:
											unexpectedState(target_node.properties.location.state);
									}
								}
								break;
							}
							default:
								unexpectedState(target_node.properties.is_valid.state);
						}
					}
					update_properties_interaction.destructor.destroy();
				};
				await clientWorkIsDone();
				if (target_node.destroyed === true) return;

				let can_finish = false;
				let updateCanFinish: (b: boolean) => void = () => {
					// noop
				};
				let checkCanFinish = () => {
					updateCanFinish(can_finish);
				};

				const openView = async () => {
					if (view_definition === undefined) {
						await onTargetDone();
						return;
					} else if (can_use_current_view(target_node)) {
						update_properties_interaction.save = false;
						await onTargetDone();
						update_properties_interaction.on_interactive(target_node);
						return;
					}
					update_properties_interaction.on_interactive(target_node);
					const view = openViewOnNode(
						view_context_node,
						view_definition.properties.view.ref,
						view_definition.properties.window.ref
					);
					const active_action = view.properties.action_active.set("yes", {
						"can finish step": ["yes", {}],
						"last step": flags.last_step ? "yes" : "no",
					});
					const action_view = active_action as unknown as ActionView;
					updateCanFinish = (predicate) => {
						if (predicate) {
							active_action.properties.can_finish_step.ensure("yes", {});
						} else {
							active_action.properties.can_finish_step.set("no", {});
						}
					};
					view.onDestroy(() => {
						updateCanFinish = () => {
							// Do nothing
						};
					});
					checkCanFinish();
					return new Promise<void>((resolve, reject) => {
						action_view.finish = async () => {
							try {
								await onTargetDone();
								getWindowInstance(view.parent.key.entry).close_view(view);
								resolve();
							} catch (reason) {
								reject(reason);
							}
						};
						action_view.cancel = () => {
							getWindowInstance(view.parent.key.entry).close_view(view);
							rollback(update_properties_interaction.history);
							switch (target_node.properties.location.state.name) {
								case "local":
									break;
								case "remote": {
									edit.switchOff(target_node);
									break;
								}
								default:
									unexpectedState(target_node.properties.location.state);
							}

							reject("Cancelled");
						};

						const can_continue = action.properties.executing.cast("yes").properties.can_continue.ensure("yes", {});
						(can_continue as unknown as Action).continue = () => {
							getWindowInstance(view.parent.key.entry).activate_view(view, true);
						};
					});
				};

				if (node_must_be_validated) {
					let node_valid = true;
					let key_unique = true;
					//check the valid status of all started actions and commands.
					checkCanFinish = () => {
						can_finish = key_unique && node_valid;
						updateCanFinish(can_finish);
					};
					const valid_subscription = target_node.properties.is_valid.subscribe({
						on_update: () => {
							target_node.properties.is_valid.switch({
								yes: () => {
									node_valid = true;
									checkCanFinish();
								},
								no: () => {
									node_valid = false;
									checkCanFinish();
								},
							});
						},
					});
					update_properties_interaction.destructor.onDestroy(() => {
						valid_subscription.unsubscribe();
					});
					const view_entity = target_node.input.entity();
					switch (view_entity.variant.name) {
						case "entry": {
							const entry = view_entity.variant.definition;
							const key = entry.properties.key.inferences.key();
							const entry_subscription = key.properties.key_unique.subscribe({
								on_update: () => {
									switch (key.properties.key_unique.state.name) {
										case "no": {
											key_unique = false;
											break;
										}
										case "yes": {
											key_unique = true;
											break;
										}
										default:
											unexpectedState(key.properties.key_unique.state);
									}
									checkCanFinish();
								},
							});
							update_properties_interaction.destructor.onDestroy(() => {
								entry_subscription?.unsubscribe();
							});
							break;
						}
						case "root":
							break;
					}
				} else {
					can_finish = true;
				}
				switch (update_properties_interaction.interactive.properties.interactive.state.name) {
					case "no": {
						if (can_finish) {
							await onTargetDone();
						} else {
							await openView();
						}
						break;
					}
					case "yes": {
						if (node_must_be_validated) {
							await openView();
						} else {
							await onTargetDone();
						}
						break;
					}
				}
			});
		},
	};
}

export function finishAction(action: api_cs.Cyes__action_active): void {
	(action as unknown as ActionView).finish?.();
}

export function cancelAction(action: api_cs.Cyes__action_active): void {
	if (confirm(translate("Discard your changes?"))) {
		//FIXME move this to the controls
		(action as unknown as ActionView).cancel?.();
	}
}

export function continueAction(action: api_cs.Cyes__can_continue): void {
	(action as unknown as Action).continue?.();
}

type ActionView = {
	finish?: () => void;
	cancel?: () => void;
};

type Action = {
	continue?: () => void;
};

type ObjectTarget = api_cs.Cnode;
type StateGroupTarget = api_cs.Cstate_group__type__property;
type ScalarTarget = api_cs.Cnumber__type__property | api_cs.Ctext__type__property | api_cs.Cfile__type__property;
type CollectionTarget = api_cs.Ccollection;
type ActionTarget = api_cs.Cyes__started;
type UiTarget = ObjectTarget | StateGroupTarget | ScalarTarget | CollectionTarget | ActionTarget;

type Mutation =
	| [
			"update text",
			{
				property: api_cs.Ctext__type__property;
				"old value": string | undefined;
				"new value": string;
			}
	  ]
	| [
			"update number",
			{
				property: api_cs.Cnumber__type__property;
				"old value": number | undefined;
				"new value": number;
			}
	  ]
	| [
			"update stategroup",
			{
				"old state": api_cs.Cstates__state_group__type__property | undefined;
				"new state": api_cs.Cstates__state_group__type__property;
			}
	  ]
	| ["create entry", api_cs.Centries__collection]
	| ["delete entry", api_cs.Centries__collection];
type History = {
	events: Mutation[];
};

async function update_text(text: api_cs.Ctext__type__property, value: string, history: History): Promise<void> {
	let old_value: string | undefined;
	if (textPropertyModifiedByUser(text)) {
		old_value = Text.getValue(text);
	} else {
		old_value = undefined;
	}
	history.events.push([
		"update text",
		{
			property: text,
			"old value": old_value,
			"new value": value,
		},
	]);
	await registrationInitialized(text);
	return iText(text).update(value, "user");
}

async function update_number(number: api_cs.Cnumber__type__property, value: number, history: History): Promise<void> {
	let old_value: number | undefined;
	if (numberPropertyModifiedByUser(number)) {
		old_value = Number.getValue(number);
	} else {
		old_value = undefined;
	}
	history.events.push([
		"update number",
		{
			property: number,
			"old value": old_value,
			"new value": value,
		},
	]);
	await registrationInitialized(number);
	update.number(number, value, "user");
}

async function update_stategroup(state: api_cs.Cstates__state_group__type__property, history: History): Promise<void> {
	const stategroup = state.parent;
	let old_state: api_cs.Cstates__state_group__type__property | undefined;
	if (stategroupPropertyModifiedByUser(stategroup)) {
		old_state = iStategroup(stategroup).getSelectedState();
	} else {
		old_state = undefined;
	}
	history.events.push([
		"update stategroup",
		{
			"old state": old_state,
			"new state": state,
		},
	]);
	await registrationInitialized(stategroup);
	iState(state).select("user");
}

async function rollback(history: History): Promise<void> {
	for (const item of history.events.reverse()) {
		switch (item[0]) {
			case "update text": {
				if (item[1].property.destroyed) continue;
				const text = item[1].property;
				const old_value = item[1]["old value"];
				if (old_value === undefined) {
					iText(text).discard();
				} else {
					await iText(text).update(old_value, "user");
				}
				break;
			}
			case "update number": {
				if (item[1].property.destroyed) continue;
				const number = item[1].property;
				const old_value = item[1]["old value"];
				if (old_value === undefined) {
					update.unset_number(number);
				} else {
					update.number(number, old_value, "user");
				}
				break;
			}
			case "update stategroup": {
				if (item[1]["new state"].parent.destroyed) continue;
				const old_state = item[1]["old state"];
				const new_state = item[1]["new state"];
				if (old_state === undefined) {
					iStategroup(new_state.parent).discard();
				} else {
					iState(old_state).select("user");
				}
				break;
			}
			case "create entry": {
				if (item[1].destroyed) continue;
				const entry = item[1];
				switch (entry.properties.location.state.name) {
					case "remote":
						// Do not roll back saved changes
						break;
					case "local":
						deleteCollectionEntry(entry);
						break;
				}
				break;
			}
			case "delete entry": {
				if (item[1].destroyed) continue;
				const entry = item[1];
				if (!entry.destroyed) {
					switch (entry.properties.location.state.name) {
						case "remote":
							entry.properties.location.state.node.properties.marked_for_deletion.ensure("no", {});
							break;
						case "local":
							break;
						default:
							unexpectedState(entry.properties.location.state);
					}
				}
				break;
			}
			default:
				unexpectedState(item[0]);
		}
	}
}

async function executeActionExpression(
	expression: api_gui.Cgui_action_expression,
	on_target: InteractionBuilder,
	this_node: api_cs.Cnode,
	target: UiTarget,
	parameter: api_cs.Cnode,
	variable: Variable | null,
	destructor: DestructorTree,
	view_bindings: Record<string, api_views.Cview_bindings>,
	history: History,
	updating_properties: boolean
): Promise<void> {
	switch (expression.properties.operation.state.name) {
		case "node switch": {
			const node_switch = expression.properties.operation.state.node;
			const node = await awaitCondition(
				expressions.node_expression(node_switch.inferences.node_switch().properties.expression)([this_node, variable]),
				(value) => value !== Pending && !(value === Unknown && SubscriptionManager.size() > 0),
				destructor
			);
			switch (node) {
				case Unknown:
					return undefined;
				case Failed:
					await executeActionExpression(
						node_switch.properties.cases.properties.false_case.properties.expression,
						on_target,
						this_node,
						target,
						parameter,
						variable,
						destructor,
						view_bindings,
						history,
						updating_properties
					);
					break;
				case Pending:
					break;
				default: {
					registerNodeContext(node, destructor);
					const variable_assignment =
						node_switch.inferences.node_switch().properties.cases.properties.node_case.properties.variable_assignment;
					const new_variable = optional_variable_assignment(variable_assignment, variable, node);
					await executeActionExpression(
						node_switch.properties.cases.properties.true_case.properties.expression,
						on_target,
						this_node,
						target,
						parameter,
						new_variable,
						destructor,
						view_bindings,
						history,
						updating_properties
					);
				}
			}
			break;
		}
		case "ignore": {
			break;
		}
		case "walk": {
			const walk = expression.properties.operation.state.node;
			const walk_expression = walk.inferences.walk();
			const node = await awaitCondition(
				expressions.singular_node_path(walk_expression.properties.path)([this_node, variable]),
				valueOrFailed,
				destructor
			);
			if (node === Failed) {
				return undefined; //TODO reject?
			}
			registerNodeContext(node, destructor);
			const entries = await entriesOf(node, walk_expression.properties.collection.entry);
			for (let entry of entries) {
				if (entry instanceof IdPathBuilder) {
					entry = registerIdPath(entry, destructor);
				}
				const filtered_entry = await awaitCondition(
					expressions.node_path_tail(walk_expression.properties.tail)(entry),
					valueOrFailed
				);
				if (!isResolved(filtered_entry)) continue;

				const new_variable = optional_variable_assignment(
					walk_expression.properties.variable_assignment,
					variable,
					filtered_entry
				);
				switch (walk_expression.properties.collection.ref.properties.ui.properties.size.state.name) {
					case "large": {
						updating_properties = false;
						break;
					}
					case "small": {
						break;
					}
				}
				await executeActionExpression(
					walk.properties.expression,
					on_target,
					this_node,
					target,
					parameter,
					new_variable,
					destructor,
					view_bindings,
					history,
					updating_properties
				);
			}
			break;
		}
		case "state switch": {
			const $ = expression.properties.operation.state.node;
			const state_switch = $.inferences.state_switch();
			const node = await awaitCondition(
				expressions.ui_parametrized_node_path(state_switch.properties.path.properties.path)([this_node, variable]),
				valueOrFailed,
				destructor
			);
			if (node === Failed) {
				return Promise.resolve();
				//TODO or throw error?
			}
			registerNodeContext(node, destructor);
			const stategroup = state_switch.properties.path.properties.stategroup.ref;
			const cs_stategroup = registerStategroup(stategroup, node, destructor);
			await registrationInitialized(cs_stategroup);
			const cs_state = await Stategroup.subscribeToSelectedState(cs_stategroup, destructor, async (state) => state);
			if (!cs_state) return Promise.resolve();
			const state = $.properties.states.get(cs_state.key.entry)!;
			const new_variable = optional_variable_assignment(
				state.key.ref.properties.variable_assignment,
				variable,
				cs_state.properties.node
			);
			await executeActionExpression(
				state.properties.expression,
				on_target,
				this_node,
				target,
				parameter,
				new_variable,
				destructor,
				view_bindings,
				history,
				updating_properties
			);
			break;
		}
		case "update properties": {
			const update_properties = expression.properties.operation.state.node;
			const singular_node_path_stream = expressions.singular_node_path(
				update_properties.inferences.update_properties()!.properties.path
			)([this_node, variable]);
			const node = await awaitCondition(singular_node_path_stream, valueOrFailed, destructor);
			if (node === Failed) {
				return undefined; //TODO reject?
			}
			registerNodeContext(node, destructor);
			switch (update_properties.properties.target.state.name) {
				case "node": {
					const node_target = update_properties.properties.target.state.node;
					on_target.show_target(
						update_properties.inferences.update_properties().properties.visualization,
						node_target.properties.bind_view.ref,
						node
					);
					await on_target.update_properties({
						action_view: node_target.properties.bind_view.ref,
						target_node: node,
						interactive: update_properties.inferences.update_properties().properties.interaction,
						destructor: destructor.addChild(),
						save: true,
						history: history,
						on_interactive: (view_context_node) => {
							edit.switchOn(view_context_node);
						},
					});
					await executeObjectExpression(
						node_target.properties.expression,
						on_target,
						this_node,
						node,
						parameter,
						variable,
						destructor,
						view_bindings,
						history,
						true
					);
					break;
				}
				case "property": {
					const property_target = update_properties.properties.target.state.node;
					const property_reference = property_target.inferences.property().properties.property.ref;
					let new_target: UiTarget;
					let view: api_gui.Cviews | undefined;
					let interactive_callback: () => void;
					switch (property_target.properties.action_view.state.name) {
						case "available": {
							view = property_target.properties.action_view.state.node.properties.bind_view.ref;
							break;
						}
						case "unavailable":
							break;
					}

					let should_update_properties = true;
					switch (property_reference.properties.type.state.name) {
						case "collection": {
							new_target = registerCollection(property_reference.properties.type.state.node, node, destructor);
							interactive_callback = () => {
								// Do nothing for collections.
							};
							should_update_properties = false;
							break;
						}
						case "text": {
							const text_property = registerText(property_reference.properties.type.state.node, node, destructor);
							new_target = text_property;
							interactive_callback = () => {
								edit.textOn(text_property);
							};
							break;
						}
						case "number": {
							const number_property = registerNumber(property_reference.properties.type.state.node, node, destructor);
							new_target = number_property;
							interactive_callback = () => {
								edit.numberOn(number_property);
							};
							break;
						}
						case "state group": {
							const stategroup_property = registerStategroup(
								property_reference.properties.type.state.node,
								node,
								destructor
							);
							new_target = stategroup_property;
							interactive_callback = () => {
								edit.stategroupOn(stategroup_property);
							};
							break;
						}
						case "group": {
							const group_node = registerGroup(property_reference.properties.type.state.node, node, destructor)
								.properties.node;
							new_target = group_node;
							interactive_callback = () => {
								edit.switchOn(group_node);
							};
							break;
						}
						case "file": {
							throw new Error("file property is not a valid property target");
						}
					}

					on_target.show_target(update_properties.inferences.update_properties().properties.visualization, view, node);
					if (!updating_properties && should_update_properties) {
						await on_target.update_properties({
							action_view: view,
							target_node: node,
							interactive: update_properties.inferences.update_properties().properties.interaction,
							destructor: destructor,
							save: true,
							history: history,
							on_interactive: interactive_callback,
						});
					}
					await executeActionExpression(
						property_target.properties.expression,
						on_target,
						this_node,
						new_target,
						parameter,
						variable,
						destructor,
						view_bindings,
						history,
						should_update_properties
					);
					break;
				}
				default:
					unexpectedState(update_properties.properties.target.state);
			}
			break;
		}
		case "execute operation": {
			const execute_operation = expression.properties.operation.state.node;
			const node = await awaitCondition(
				expressions.singular_node_path(execute_operation.inferences.execute_operation().properties.path)([
					this_node,
					variable,
				]),
				valueOrFailed,
				destructor
			);
			if (node === Failed) {
				return undefined; //TODO reject?
			}
			registerNodeContext(node, destructor);
			const operation_type = execute_operation.inferences.execute_operation().properties.operation.ref;
			operation_type.properties.type.switch({
				action: ($) => registerAction($, node, destructor),
				command: ($) =>
					$.properties.type.switch({
						global: ($) => registerCommand($, node, destructor),
						component: () => undefined,
					}),
				"reference set": undefined,
				property: undefined,
			});
			const operation_attribute = node.properties.attributes.get(operation_type.key)!;
			const started_operation = startOperation(operation_attribute.properties.type.cast("operation"), destructor);
			const parameters_node = started_operation.properties.node;
			const execute_operation_destructor = destructor.addChild();
			await on_target.update_properties({
				target_node: parameters_node,
				interactive: execute_operation.inferences.execute_operation().properties.interaction,
				action_view: execute_operation.properties.bind_view.ref,
				destructor: execute_operation_destructor,
				save: false,
				history: history,
				on_interactive: () => {
					// No need to do anything for operations
				},
			});
			execute_operation.properties.operation.switch({
				command: () => {
					on_target.execute_command(started_operation);
				},
				action: () => {
					on_target.execute_action({
						action: started_operation,
					});
				},
			});
			await executeObjectExpression(
				execute_operation.properties.expression,
				on_target,
				this_node,
				parameters_node,
				parameter,
				variable,
				execute_operation_destructor,
				view_bindings,
				history,
				updating_properties
			);
			break;
		}
		case "produce value": {
			const produce_value = expression.properties.operation.state.node;
			switch (produce_value.properties.value.state.name) {
				case "object": {
					const object_target = produce_value.properties.value.state.node;
					await executeObjectExpression(
						object_target.properties.expression,
						on_target,
						this_node,
						target,
						parameter,
						variable,
						destructor,
						view_bindings,
						history,
						updating_properties
					);
					break;
				}
				case "state group": {
					const state_group_target = produce_value.properties.value.state.node;
					const state_to_select = state_group_target.inferences.state_group().properties.state.entry;
					const target_state_group = state_group_target.inferences.state_group().inferences.target_state_group();
					switch (target_state_group.variant.name) {
						case "stategroup property": {
							const state_group = target as api_cs.Cstate_group__type__property;
							switch (state_group.properties.is_editable.state.name) {
								case "no":
									break;
								case "yes": {
									const state = state_group.properties.states.get(state_to_select)!;
									await update_stategroup(state, history);
									await executeObjectExpression(
										state_group_target.properties.expression,
										on_target,
										this_node,
										state.properties.node,
										parameter,
										variable,
										destructor,
										view_bindings,
										history,
										updating_properties
									);
									break;
								}
								default:
									unexpectedState(state_group.properties.is_editable.state);
							}
							break;
						}
						default:
							unexpectedState(target_state_group.variant.name);
					}
					break;
				}
				case "scalar": {
					const scalar_target = produce_value.properties.value.state.node;
					const expression = scalar_target.inferences.scalar().properties.expression;
					const target_scalar = scalar_target.inferences.scalar().inferences.target_scalar();
					switch (target_scalar.variant.name) {
						case "property value": {
							const $ = target_scalar.variant.definition;
							switch ($.variant.name) {
								case "number": {
									const number$ = $.variant.definition;
									switch (number$.variant.name) {
										case "intermediate result":
											break;
										case "number property": {
											const number_property = target as api_cs.Cnumber__type__property;
											const value = await awaitCondition(
												expressions.ui_scalar_value_expression<number>(expression)([
													this_node,
													variable,
													number_property.parent,
												]),
												valueOrFailed
											);
											if (value === Failed) {
												if (!numberPropertyModifiedByUser(number_property)) {
													update.unset_number(number_property);
												}
											} else {
												await update_number(number_property, value, history);
											}
											break;
										}
										default:
											unexpectedState(number$.variant);
									}
									break;
								}
								case "text": {
									const text$ = $.variant.definition;
									switch (text$.variant.name) {
										case "intermediate result":
											break;
										case "text property": {
											const text_property = target as api_cs.Ctext__type__property;
											const value = await awaitCondition(
												expressions.ui_scalar_value_expression<expressions.StringLike>(expression)([
													this_node,
													variable,
													text_property.parent,
												]),
												valueOrFailed
											);
											if (value === Failed) {
												if (!textPropertyModifiedByUser(text_property)) {
													iText(text_property).discard();
												}
											} else {
												await update_text(text_property, value.toString(), history);
											}
											break;
										}
									}
									break;
								}
								case "file": {
									const file_property = target as api_cs.Cfile__type__property;
									const value = await awaitCondition(
										expressions.ui_scalar_value_expression<expressions.FileToken>(expression)([
											this_node,
											variable,
											file_property.parent,
										]),
										valueOrFailed
									);
									if (value !== Failed) {
										update.fileToken(file_property, value);
									}
									break;
								}
								case "state choice": {
									const state_choice = $.variant.definition;
									switch (state_choice.variant.name) {
										case "state node choice": {
											const stategroup_property = target as api_cs.Cstate_group__type__property;
											const value = await awaitCondition(
												expressions.ui_scalar_value_expression<expressions.StateChoice>(expression)([
													this_node,
													variable,
													stategroup_property.parent,
												]),
												valueOrFailed
											);
											if (value === Failed) {
												if (!stategroupPropertyModifiedByUser(stategroup_property)) {
													iStategroup(stategroup_property).discard();
												}
											} else {
												const state_to_set = stategroup_property.properties.states.get(value.key)!;
												await update_stategroup(state_to_set, history);
											}
											break;
										}
										default:
											unexpectedState(state_choice.variant.name);
									}

									break;
								}
								default:
									unexpectedState($.variant);
							}
							break;
						}
						case "style":
							break;
						default:
							unexpectedState(target_scalar.variant);
					}
					break;
				}
				default:
					unexpectedState(produce_value.properties.value.state);
			}
			break;
		}
		case "entry list": {
			const $ = expression.properties.operation.state.node;
			await executeEntryExpressionList(
				$.properties.entries,
				on_target,
				this_node,
				target,
				parameter,
				variable,
				destructor,
				view_bindings,
				history,
				updating_properties
			);
			break;
		}
		case "entry": {
			const entry_expression = expression.properties.operation.state.node;
			switch (entry_expression.properties.type.state.name) {
				case "create": {
					const create_entry = entry_expression.properties.type.state.node;
					switch (create_entry.properties.collection_type.state.name) {
						case "parameter": {
							const collection = target as api_cs.Ccollection;
							const entry = add.addCollectionEntry(collection);
							registerEntry(entry, destructor);
							await executeObjectExpression(
								create_entry.properties.expression,
								on_target,
								this_node,
								entry.properties.node,
								parameter,
								variable,
								destructor.addChild(),
								view_bindings,
								history,
								updating_properties
							);
							break;
						}
						case "node": {
							const node_collection = create_entry.properties.collection_type.state.node;
							const collection = target as api_cs.Ccollection;
							const new_entry = iCollection(collection).addEntry("user");
							registerEntry(new_entry, destructor);
							iCollection(collection).clearDefaultEntries();
							history.events.push(["create entry", new_entry]);
							const new_node = new_entry.properties.node;
							registerNewNode(new_node, destructor);
							on_target.show_target(
								create_entry.inferences.create().properties.visualization,
								node_collection.properties.bind_view.ref,
								new_node
							);
							if (!updating_properties) {
								await on_target.update_properties({
									action_view: node_collection.properties.bind_view.ref,
									target_node: new_node,
									interactive: create_entry.inferences.create().properties.interaction,
									destructor: destructor.addChild(),
									save: true,
									history: history,
									on_interactive: (view_context_node) => {
										edit.switchOn(view_context_node);
									},
								});
							}
							await executeObjectExpression(
								create_entry.properties.expression,
								on_target,
								this_node,
								new_node,
								parameter,
								variable,
								destructor,
								view_bindings,
								history,
								true
							);
							break;
						}
						default:
							unexpectedState(create_entry.properties.collection_type.state);
					}
					break;
				}
				case "delete": {
					const delete_entry = entry_expression.properties.type.state.node;
					const collection = target as api_cs.Ccollection;
					const view_context_node = collection.parent.parent.parent;
					await on_target.update_properties({
						action_view: delete_entry.properties.bind_view.ref,
						target_node: view_context_node,
						interactive: delete_entry.inferences.delete_().properties.interaction,
						destructor: destructor.addChild(),
						save: true,
						history: history,
						on_interactive: () => {
							// Do nothing on delete?
						},
					});
					const node = await awaitCondition(
						expressions.singular_node_path(delete_entry.inferences.delete_().properties.path)([this_node, variable]),
						valueOrFailed,
						destructor
					);
					if (node === Failed) {
						return undefined;
					}
					registerNodeContext(node, destructor);
					const entry = node.input.position().cast("entity").cast("entry");
					history.events.push(["delete entry", entry]);
					deleteCollectionEntry(entry);
					break;
				}
				default:
					unexpectedState(entry_expression.properties.type.state);
			}

			break;
		}
		default:
			unexpectedState(expression.properties.operation.state);
	}
}

async function executeObjectExpression(
	expression: api_gui.Cobject_expression,
	on_target: InteractionBuilder,
	this_node: api_cs.Cnode,
	target: UiTarget,
	parameter: api_cs.Cnode,
	variable: Variable | null,
	destructor: DestructorTree,
	view_bindings: Record<string, api_views.Cview_bindings>,
	history: History,
	updating_properties: boolean
): Promise<void> {
	await expression.input
		.object_expression()
		.input.target()
		.switch({
			node: (node_target) => {
				const context_node = target as api_cs.Cnode;
				return promiseSeries(
					orderedAttributes(node_target).map((attribute_name) => async () => {
						switch (attribute_name.properties.type.state.name) {
							case "action":
								break;
							case "command":
								break;
							case "reference set":
								break;
							case "property": {
								const object_expression_property = expression.properties.properties.get(attribute_name.key);
								if (object_expression_property) {
									const property_def = context_node.input
										.node_type()
										.properties.attributes.get(attribute_name.key)!
										.properties.type.cast("property");
									await executeActionExpression(
										object_expression_property.properties.default_,
										on_target,
										this_node,
										object_expression_property.key.ref.key.ref.properties.type.switch<UiTarget>({
											collection: () =>
												addClientState.addCollectionAttribute(
													property_def.properties.type.cast("collection"),
													context_node
												),
											file: () => {
												throw new Error("not supported");
											},
											group: () =>
												registerGroup(property_def.properties.type.cast("group"), context_node, destructor).properties
													.node,
											"state group": () =>
												registerStategroup(property_def.properties.type.cast("state group"), context_node, destructor),
											number: () =>
												registerNumber(property_def.properties.type.cast("number"), context_node, destructor),
											text: () => registerText(property_def.properties.type.cast("text"), context_node, destructor),
										}),
										parameter,
										variable,
										destructor,
										view_bindings,
										history,
										updating_properties
									);
								}
								break;
							}
							default:
								unexpectedState(attribute_name.properties.type.state);
						}
					})
				);
			},
		});
}

async function executeEntryExpressionList(
	expression: api_gui.Centry_expression_list,
	on_target: InteractionBuilder,
	this_node: api_cs.Cnode,
	target: UiTarget,
	parameter: api_cs.Cnode,
	variable: Variable | null,
	destructor: DestructorTree,
	view_bindings: Record<string, api_views.Cview_bindings>,
	history: History,
	updating_properties: boolean
): Promise<void> {
	await executeActionExpression(
		expression.properties.expression,
		on_target,
		this_node,
		target,
		parameter,
		variable,
		destructor,
		view_bindings,
		history,
		updating_properties
	);
	switch (expression.properties.more_entries.state.name) {
		case "yes": {
			const $ = expression.properties.more_entries.state.node;
			await executeEntryExpressionList(
				$.properties.tail,
				on_target,
				this_node,
				target,
				parameter,
				variable,
				destructor,
				view_bindings,
				history,
				updating_properties
			);
			break;
		}
		case "no":
			break;
		default:
			unexpectedState(expression.properties.more_entries.state);
	}
}

function numberPropertyModifiedByUser(cs_number: api_cs.Cnumber__type__property) {
	return cs_number.properties.is_set.switch({
		no: false,
		yes: ($) =>
			$.properties.by.switch({
				engine: false,
				user: true,
			}),
	});
}

function textPropertyModifiedByUser(cs_text: api_cs.Ctext__type__property) {
	return cs_text.properties.is_set.switch({
		no: false,
		yes: ($) =>
			$.properties.by.switch({
				engine: false,
				user: true,
			}),
	});
}

function stategroupPropertyModifiedByUser(cs_stategroup: api_cs.Cstate_group__type__property) {
	return cs_stategroup.properties.is_set.switch({
		no: false,
		yes: ($) =>
			$.properties.by.switch({
				engine: false,
				user: true,
			}),
	});
}

function remoteEntries(id_path: IdPathBuilder, collection_name: string): Promise<IdPathBuilder[]> {
	const builder = SubscriptionBuilder.create();
	builder.collection_entries(collection_name);
	return new Promise<IdPathBuilder[]>((resolve) => {
		DatastoreProxy.instance.subscribeOnce(
			{
				"context node": id_path.build(),
				"mutation permissions": ["ignore", {}],
				"node subscription": { attributes: builder.properties },
			},
			(subscribed) => {
				resolve(
					subscribed.properties.context.switch({
						"does not exist": () => [],
						"permission denied": () => [],
						exists: (existing_context) =>
							existing_context.properties.subscription_result_data.properties.read_permitted.switch({
								yes: (yes) =>
									Array.from(
										yes.properties.attributes
											.get(collection_name)!
											.properties.type.cast("property")
											.properties.type.cast("collection")
											.properties.result.cast("entries").properties.entries
									).map((entry) => {
										return id_path.collectionEntry(collection_name, entry[1].key);
									}),
								no: () => [],
							}),
					})
				);
			}
		);
	});
}

async function entriesOf(node: api_cs.Cnode, collection: string): Promise<(api_cs.Cnode | IdPathBuilder)[]> {
	let result: (api_cs.Cnode | IdPathBuilder)[] = [];
	const getRemoteEntries = async (id_path: IdPathBuilder) => {
		result = await remoteEntries(id_path, collection);
	};

	const attribute = node.properties.attributes.get(collection);
	if (attribute !== undefined) {
		const collection_property = attribute.properties.type.cast("property").properties.type.cast("collection");
		const getLocalEntries = () => {
			for (const entry of collection_property.properties.entries.values()) {
				switch (entry.properties.location.state.name) {
					case "remote": {
						break;
					}
					case "local": {
						result.push(entry.properties.node);
						break;
					}
					default:
						unexpectedState(entry.properties.location.state);
				}
			}
		};
		const initialized = Collection.isInitialized(collection_property);
		switch (initialized) {
			case false: {
				await getRemoteEntries(createIdPathBuilderFromNode(node));
				getLocalEntries();
				break;
			}
			case true: {
				for (const entry of collection_property.properties.entries.values()) {
					result.push(entry.properties.node);
				}
				break;
			}
			default:
				unexpectedState(initialized);
		}
	} else {
		await getRemoteEntries(createIdPathBuilderFromNode(node));
	}
	return result;
}

// Fixme do this in the generator?
/// Returns the ancestor node that acts as an action boundry. This relies heavily on the generated desktop UI. But it
/// the current view can be used to handle an action step.
function actionBoundry(node: api_cs.Cnode, view_node_type: api_app.Cnode__application): api_cs.Cnode {
	if (node.input.node_type() === view_node_type) return node;
	const position = node.input.position();
	switch (position.variant.name) {
		case "entity": {
			const entity = position.variant.definition;
			switch (entity.variant.name) {
				case "root":
					return node;
				case "entry": {
					const entry = entity.variant.definition;
					const collection = entry.parent;
					const collection_context_node = collection.parent.parent.parent;

					const small = collection.inferences.collection_type().properties.ui.properties.size;
					switch (small.state.name) {
						case "small": {
							const breakout = collection.inferences.collection_type().properties.ui.properties.break_out;
							switch (breakout.state.name) {
								case "yes": {
									return collection_context_node;
								}
								case "no": {
									return actionBoundry(collection_context_node, view_node_type);
								}
							}
							break;
						}
						case "large": {
							return node;
						}
					}
				}
			}
			break;
		}
		case "group": {
			const group = position.variant.definition;
			const breakout = group.inferences.group_type().properties.ui.properties.break_out;
			switch (breakout.state.name) {
				case "yes": {
					return node;
				}
				case "no": {
					return actionBoundry(group.parent.parent.parent, view_node_type);
				}
			}
			break;
		}
		case "root": {
			return node;
		}
		case "state": {
			const state = position.variant.definition;
			return actionBoundry(state.parent.parent.parent.parent, view_node_type);
		}
	}
}
